// Per-controller state, the two THREE arrays (target-ray + grip spaces),
// the model factory, plus the helpers everything else touches:
// `pulseHaptic` (taps the gamepad's haptic actuator) and
// `findControllerIndexByHandedness` (left/right → controllerStates index).
//
// `controllers` / `grips` are populated by main.ts's controller setup loop
// (which calls `renderer.xr.getController(i)` etc); they live here so any
// downstream module that needs to read a controller can `import` directly
// instead of going through main.ts.

import * as THREE from "three";
import { XRControllerModelFactory } from "three/addons/webxr/XRControllerModelFactory.js";

export interface ControllerState {
  // Boolean derived from the squeezestart/squeezeend WebXR events.
  // Fires at whatever partial-depression threshold the runtime uses,
  // so this is the right signal for "is the grip engaged at all"
  // (e.g., two-grip nav).
  squeezing: boolean;
  // Analog squeeze pressure, 0..1, read from `gamepad.buttons[1].value`
  // each frame. Use this when you need "fully depressed" specifically
  // (e.g., the laser-tilt gesture only engages at near-full pressure
  // so a light squeeze for nav doesn't accidentally tilt the laser).
  squeezeValue: number;
  selecting: boolean;
  // Face buttons. On Meta Touch the right controller has A (button 4) and
  // B (button 5); the left has X (4) and Y (5). aHeld/bHeld track buttons
  // 4 and 5 of whatever controller this slot is bound to.
  aHeld: boolean;
  bHeld: boolean;
  inputSource: XRInputSource | null;
}

export const controllerStates: ControllerState[] = [
  { squeezing: false, squeezeValue: 0, selecting: false, aHeld: false, bHeld: false, inputSource: null },
  { squeezing: false, squeezeValue: 0, selecting: false, aHeld: false, bHeld: false, inputSource: null },
];

// Threshold on the analog squeeze pressure above which the grip is
// considered "fully depressed". Used by the laser-tilt gesture so a
// light squeeze for two-grip nav doesn't accidentally tilt the laser.
// Set high (0.99) because Meta runtimes map grip pressure non-
// linearly — `value` reaches ~0.9 well before the user feels they've
// bottomed out.
export const FULL_SQUEEZE_THRESHOLD = 0.99;

export const controllerModelFactory = new XRControllerModelFactory();
export const controllers: THREE.XRTargetRaySpace[] = [];
export const grips: THREE.XRGripSpace[] = [];

export function pulseHaptic(
  i: number,
  intensity: number,
  durationMs: number,
): void {
  const src = controllerStates[i].inputSource;
  const actuator = src?.gamepad?.hapticActuators?.[0];
  actuator?.pulse?.(intensity, durationMs);
}

export function findControllerIndexByHandedness(hand: XRHandedness): number {
  for (let i = 0; i < controllerStates.length; i++) {
    if (controllerStates[i].inputSource?.handedness === hand) return i;
  }
  return -1;
}

// Squeeze on a controller tilts the laser this many radians down from
// its default forward (-Z) direction. Lets the user point at a face
// below their hand without contorting their wrist.
export const LASER_TILT_SQUEEZE = Math.PI / 4;

// Precision-aim state. Engaged by the non-dominant index trigger
// (see main.ts wiring). While active, the controller's effective
// rotation is `entry + PRECISION_AIM_GAIN × (current − entry)` —
// i.e., a fraction of the controller's rotation delta from the
// press-time orientation. Small wrist movements translate to even
// smaller cursor movements, giving the user a Wacom-style fine-aim
// for picking dense / distant voxels. Translation is unaffected.
// Releasing the trigger snaps the laser back to the controller's
// actual direction.
let _precisionActive = false;
let _precisionControllerIdx = -1;
const _precisionEntryWorldQuat = new THREE.Quaternion();
const _precisionEntryWorldPos = new THREE.Vector3();

// Fraction of normal aim sensitivity while precision mode is held.
// 0.15 = move controller 90° → laser moves ~13.5°. Tune by feel.
export const PRECISION_AIM_GAIN = 0.15;

// Scratch buffers used by precisionLocalQuat / laserLocalQuat /
// controllerEffectiveWorldPos — module-scoped so per-frame calls
// don't allocate.
const _precisionCurWorld = new THREE.Quaternion();
const _precisionDelta = new THREE.Quaternion();
const _precisionScaled = new THREE.Quaternion();
const _laserLocalQuatScratch = new THREE.Quaternion();
const _squeezeTiltScratch = new THREE.Quaternion();
const _tiltAxis = new THREE.Vector3(1, 0, 0);

// Toggle precision aim. Passing active=true captures the named
// controller's current world quaternion as the anchor; future calls
// to laserLocalDir / laserLocalQuat for that controller will dampen
// rotation deltas measured from the anchor. active=false releases.
export function setPrecisionAim(
  active: boolean,
  controllerIndex: number,
): void {
  if (active) {
    if (controllerIndex < 0 || controllerIndex >= controllers.length) return;
    const c = controllers[controllerIndex];
    if (!c) return;
    c.getWorldQuaternion(_precisionEntryWorldQuat);
    c.getWorldPosition(_precisionEntryWorldPos);
    _precisionActive = true;
    _precisionControllerIdx = controllerIndex;
  } else {
    _precisionActive = false;
    _precisionControllerIdx = -1;
  }
}

// Write the controller's precision-adjusted world position into `out`.
// Without precision (or for a different controller), this equals the
// controller's raw world position. With precision active for this
// controller, the position is dampened toward the entry-time position:
//   effective = entryPos + (currentPos − entryPos) × PRECISION_AIM_GAIN.
// Ray-based callers (pick origin, gesture endpoints, grab-bar press)
// route their controller-position reads through here so arm motion is
// dampened in lockstep with the aim dampening.
export function controllerEffectiveWorldPos(
  controllerIndex: number,
  out: THREE.Vector3,
): void {
  const c = controllers[controllerIndex];
  if (!c) {
    out.set(0, 0, 0);
    return;
  }
  c.getWorldPosition(out);
  if (!_precisionActive || controllerIndex !== _precisionControllerIdx) {
    return;
  }
  // out := entry + (out − entry) × GAIN
  out.sub(_precisionEntryWorldPos)
    .multiplyScalar(PRECISION_AIM_GAIN)
    .add(_precisionEntryWorldPos);
}

// Local-frame override that, when post-multiplied onto the
// controller's world rotation, yields the precision-adjusted world
// rotation. Identity when precision isn't active for `controllerIndex`,
// so callers can apply it unconditionally.
function precisionLocalQuat(
  controllerIndex: number,
  out: THREE.Quaternion,
): void {
  if (!_precisionActive || controllerIndex !== _precisionControllerIdx) {
    out.identity();
    return;
  }
  const c = controllers[controllerIndex];
  if (!c) {
    out.identity();
    return;
  }
  c.getWorldQuaternion(_precisionCurWorld);
  // delta = curWorld * entry⁻¹ — rotation from entry to now in world.
  _precisionDelta
    .copy(_precisionEntryWorldQuat)
    .invert()
    .premultiply(_precisionCurWorld);
  // scaledDelta = slerp(identity, delta, GAIN) — fraction of the
  // rotation we actually apply.
  _precisionScaled.identity().slerp(_precisionDelta, PRECISION_AIM_GAIN);
  // effectiveWorld = scaledDelta * entryWorld.
  // localOverride  = curWorld⁻¹ * effectiveWorld.
  out
    .copy(_precisionCurWorld)
    .invert()
    .multiply(_precisionScaled)
    .multiply(_precisionEntryWorldQuat);
}

// Write the squeeze-tilt quaternion (rotation around local X) into
// `out`. `suppressTilt` lets the caller override the squeeze-derived
// tilt for gestures that already own the squeeze (canvas-drag) so
// the laser doesn't tilt mid-gesture.
function squeezeTiltQuat(
  controllerIndex: number,
  out: THREE.Quaternion,
  suppressTilt: boolean,
): void {
  if (suppressTilt) {
    out.identity();
    return;
  }
  const fullSqueeze =
    (controllerStates[controllerIndex]?.squeezeValue ?? 0) >=
    FULL_SQUEEZE_THRESHOLD;
  const tilt = fullSqueeze ? LASER_TILT_SQUEEZE : 0;
  out.setFromAxisAngle(_tiltAxis, -tilt);
}

// Full local-frame quaternion for the laser: precision override
// composed with the squeeze tilt. ray.line uses this as its local
// quaternion; downstream callers can also apply it to a local-frame
// direction vector (see laserLocalDir below).
//
// Compose order: localComposite = precision * tilt. Then world =
// controller.worldQuat * localComposite = effectiveWorld * tilt,
// which is the desired "precision-aimed laser, still tilted by the
// squeeze."
// `ignorePrecision` skips the precision-aim damping (uses the raw
// controller orientation, tilt only). The pick path keeps precision; this
// is for the visible laser line, so it can stay rigidly attached to the
// controller even while precision mode dampens the cursor.
export function laserLocalQuat(
  controllerIndex: number,
  out: THREE.Quaternion,
  options?: { suppressTilt?: boolean; ignorePrecision?: boolean },
): void {
  if (options?.ignorePrecision) {
    out.identity();
  } else {
    precisionLocalQuat(controllerIndex, out);
  }
  squeezeTiltQuat(
    controllerIndex,
    _squeezeTiltScratch,
    options?.suppressTilt ?? false,
  );
  out.multiply(_squeezeTiltScratch);
}

// Write the controller's current laser direction in CONTROLLER-LOCAL
// space into `out` — `(0, 0, -1)` by default, tilted down by
// `LASER_TILT_SQUEEZE` while the grip is FULLY squeezed (so a light
// squeeze for nav doesn't engage the tilt). Honors precision aim
// when active (see laserLocalQuat). Gesture handlers (extrude,
// box, move, marquee) read through this so the gesture follows the
// same direction the user sees on the laser.
export function laserLocalDir(
  controllerIndex: number,
  out: THREE.Vector3,
  options?: { suppressTilt?: boolean },
): void {
  laserLocalQuat(controllerIndex, _laserLocalQuatScratch, options);
  out.set(0, 0, -1).applyQuaternion(_laserLocalQuatScratch);
}
